import { Dialog, DialogBody, DialogTitle } from "@/components/catalyst/dialog";
import { Button } from "@/components/catalyst/button";
import { InvoiceStatus } from "@/gql/graphql";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { pendingInvoicesQuery } from "@/app/Dashboard/GraphQL/pendingInvoicesQuery";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { format } from "date-fns";
import {
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import { createInvoiceCheckoutSessionMutation } from "@/app/Invoices/GraphQL/invoiceMutations";
import { Suspense, useState } from "react";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";

interface OverdueInvoicesModalProps {
  isOpen: boolean;
}

export function OverdueInvoicesModal({ isOpen }: OverdueInvoicesModalProps) {
  return (
    <Dialog open={isOpen} onClose={() => {}} size="5xl">
      <DialogTitle className="flex items-center gap-3">
        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
        <span>Payment Required</span>
      </DialogTitle>
      <DialogBody>
        <Suspense>
          <Inner />
        </Suspense>
      </DialogBody>
    </Dialog>
  );
}

function Inner() {
  const { data, isLoading } = useSuspenseGqlQuery(pendingInvoicesQuery, {});
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { mutateAsync: createInvoiceCheckout, isPending } = useGqlMutation(
    createInvoiceCheckoutSessionMutation,
  );

  const overdueInvoices =
    data?.invoices.data.filter(
      (invoice) => invoice.status === InvoiceStatus.Overdue,
    ) ?? [];

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-700">
        You have{" "}
        <strong className="font-semibold text-red-600">
          {overdueInvoices.length} overdue{" "}
          {overdueInvoices.length === 1 ? "invoice" : "invoices"}
        </strong>{" "}
        that must be paid before you can continue using the system.
      </p>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
        </div>
      ) : overdueInvoices.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-gray-900">
            Overdue Invoices:
          </h3>
          <div className="max-h-96 space-y-2 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-4">
            {overdueInvoices.map((invoice) => (
              <div
                key={invoice.id}
                className="flex items-center justify-between rounded-md bg-white p-3 shadow-sm"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {invoice.reference}
                    </span>
                    <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                      Overdue
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-4 text-xs text-gray-600">
                    <span>{invoice.payee.name}</span>
                    {invoice.due_date ? (
                      <span>
                        Due: {format(new Date(invoice.due_date), "MMM d, yyyy")}
                      </span>
                    ) : null}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      {formatCurrency(invoice.amount_due, invoice.currency)}
                    </div>
                  </div>
                  <Button
                    outline
                    onClick={() => {
                      window.open(invoice.customer_url, "_blank");
                    }}
                  >
                    View <ArrowTopRightOnSquareIcon />
                  </Button>
                  <Button
                    color="blue"
                    className="text-xs"
                    isLoading={isPending}
                    onClick={async () => {
                      const res = await createInvoiceCheckout({
                        id: invoice.id,
                      });
                      window.open(
                        res.createInvoiceCheckoutSession.checkout_session_url,
                        "_blank",
                      );
                    }}
                  >
                    Pay Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between border-t border-gray-200 pt-3">
            <span className="text-sm font-medium text-gray-700">
              Total Outstanding:
            </span>
            <span className="text-lg font-bold text-red-600">
              {overdueInvoices.length > 0
                ? formatCurrency(
                    overdueInvoices.reduce(
                      (sum, inv) => sum + inv.amount_due,
                      0,
                    ),
                    overdueInvoices[0].currency,
                  )
                : null}
            </span>
          </div>
        </div>
      ) : null}

      <div className="flex justify-end gap-3 pt-2">
        <Button
          color="white"
          LeadingIcon={ArrowPathIcon}
          onClick={async () => {
            try {
              setIsRefreshing(true);
              await queryClient.invalidateQueries();
            } finally {
              setIsRefreshing(false);
            }
          }}
          disabled={isRefreshing}
        >
          {isRefreshing ? "Refreshing..." : "Refresh"}
        </Button>
      </div>
    </div>
  );
}
